import {Box, Button, debounce, Tooltip} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useCreateInvoice} from '../../Finance/InvoiceDetails/hooks';
import {TEXT_CONSTANTS} from '../../Finance/InvoiceDetails/constants';
import {
  GenerateInvoiceButtonStyles,
  GenerateInvoiceButtonWrapperStyles,
} from '../../Finance/PaymentDetails/styles';
import {ICreateInvoiceVariables} from '~/api/finance/invoice';
import {PaymentDetails} from '../../Finance/PaymentDetails/types';
import {InsuranceClaim} from '~/api/finance/insuranceClaims/insuranceClaimDetails';
import {formatDate} from '~/utils/date';
import {VALIDATION_MESSAGES} from '~/modules/frontdesk/Finance/InsuranceClaims/constants';
import {disabledButtonStyles} from '~/modules/frontdesk/Finance/InsuranceClaims/ClaimDetails/style';
import {getRoute, RouteSection} from '$utils/route';
import {RoutePath} from '$constants/routes';

interface IGenerateInvoiceButtonProps {
  paymentId?: string | undefined;
  insuranceClaimId?: string | undefined;
  payment?: PaymentDetails;
  insuranceClaim?: InsuranceClaim;
  onSuccess?: () => void;
  context?: 'calendar' | 'finance';
}

function GenerateInvoiceButton(props: IGenerateInvoiceButtonProps) {
  const navigate = useNavigate();

  const {
    paymentId,
    insuranceClaimId,
    payment,
    insuranceClaim,
    onSuccess,
    context,
  } = props;

  const {createInvoice, isDisabled} = useCreateInvoice({
    onCreateInvoiceSuccess,
  });

  function handleGenerateInvoiceClick() {
    const variables: ICreateInvoiceVariables = {};

    if (paymentId) {
      variables.payment_id = Number(paymentId);
    }

    if (insuranceClaimId) {
      variables.insurance_claim_id = Number(insuranceClaimId);
    }

    createInvoice(variables);
  }

  function redirectToInvoiceDetailsPage() {
    if (paymentId) {
      let backRoute;
      if (context === 'calendar') {
        backRoute = getRoute(
          RouteSection.Calendar,
          `${RoutePath.staff.calendar.bookingDetails}/payment/${paymentId}`
        );
      } else {
        backRoute = getRoute(
          RouteSection.Finance,
          `${RoutePath.staff.finance.payments}/${paymentId}`
        );
      }

      navigate(
        getRoute(
          RouteSection.Finance,
          `${RoutePath.staff.finance.payments}/${paymentId}/invoice`
        ),
        {
          state: {
            backRoute,
          },
        }
      );
    } else if (insuranceClaimId) {
      navigate(
        getRoute(
          RouteSection.Finance,
          RoutePath.staff.finance.insuranceClaimInvoiceDetails.replace(
            ':id',
            insuranceClaimId
          )
        )
      );
    }
  }

  function onCreateInvoiceSuccess() {
    if (onSuccess) {
      onSuccess();
    } else {
      redirectToInvoiceDetailsPage();
    }
  }

  const data = payment || insuranceClaim;
  const hasItems = data?.items && data.items.length > 0;
  const invoiceIssuedAt =
    payment?.invoice_issued_at || insuranceClaim?.invoice_issued_at;
  const buttonText = invoiceIssuedAt
    ? `Invoice: Last Update ${formatDate(invoiceIssuedAt)}`
    : TEXT_CONSTANTS.GENERATE_INVOICE;

  const isButtonDisabled = isDisabled || !hasItems;
  const tooltipText = !hasItems
    ? VALIDATION_MESSAGES.ADD_ITEMS_TO_GENERATE
    : '';

  return (
    <Box sx={GenerateInvoiceButtonWrapperStyles}>
      <Tooltip title={tooltipText} placement="top" arrow>
        <span>
          <Button
            variant="outlined"
            disabled={isButtonDisabled}
            onClick={debounce(handleGenerateInvoiceClick, 500)}
            sx={{
              ...GenerateInvoiceButtonStyles,
              ...(isButtonDisabled && disabledButtonStyles),
            }}
          >
            {buttonText}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}

export default GenerateInvoiceButton;
